import * as THREE from 'three';
import TWEEN from '@tweenjs/tween.js';
import {USE_LEGACY_TWEEN_GROUP} from './tween';

export const PREVIEW_MIN_ZOOM = 0.2;
export const PREVIEW_MAX_ZOOM = 1.5;
export const PREVIEW_FIT_COVERAGE = 0.9;

export const computeOrthographicFrustum = (
    width: number,
    height: number,
    frustumSize = 1500
) => {
    const aspect = width / height;
    return {
        left: (-frustumSize * aspect) / 2,
        right: (frustumSize * aspect) / 2,
        top: frustumSize / 2,
        bottom: -frustumSize / 2,
    };
};

/**
 * Pure post-projection math: given the scene's NDC bounds at zoom=1, derive
 * the zoom and pan needed to fit it inside PREVIEW_FIT_COVERAGE.
 * Used by product 3D preview screenshots and placeholder GLB thumbnails.
 * @param {number} xMin NDC min x at zoom 1
 * @param {number} xMax NDC max x at zoom 1
 * @param {number} yMin NDC min y at zoom 1
 * @param {number} yMax NDC max y at zoom 1
 * @return {{zoom: number, panNdcX: number, panNdcY: number} | null}
 */
export const computeFitFromBounds = (
    xMin: number,
    xMax: number,
    yMin: number,
    yMax: number
): {zoom: number; panNdcX: number; panNdcY: number} | null => {
    const ndcWidthAt1 = xMax - xMin;
    const ndcHeightAt1 = yMax - yMin;
    if (ndcWidthAt1 <= 0 || ndcHeightAt1 <= 0) return null;

    const zoomScale = Math.min(
        (2 * PREVIEW_FIT_COVERAGE) / ndcWidthAt1,
        (2 * PREVIEW_FIT_COVERAGE) / ndcHeightAt1
    );
    const zoom = Math.max(
        PREVIEW_MIN_ZOOM,
        Math.min(PREVIEW_MAX_ZOOM, zoomScale)
    );

    const xMinFit = xMin * zoom;
    const xMaxFit = xMax * zoom;
    const yMinFit = yMin * zoom;
    const yMaxFit = yMax * zoom;

    let panNdcX = 0;
    if (xMinFit < -PREVIEW_FIT_COVERAGE && xMaxFit <= PREVIEW_FIT_COVERAGE) {
        panNdcX = -PREVIEW_FIT_COVERAGE - xMinFit;
    } else if (
        xMaxFit > PREVIEW_FIT_COVERAGE &&
        xMinFit >= -PREVIEW_FIT_COVERAGE
    ) {
        panNdcX = PREVIEW_FIT_COVERAGE - xMaxFit;
    }

    let panNdcY = 0;
    if (yMinFit < -PREVIEW_FIT_COVERAGE && yMaxFit <= PREVIEW_FIT_COVERAGE) {
        panNdcY = -PREVIEW_FIT_COVERAGE - yMinFit;
    } else if (
        yMaxFit > PREVIEW_FIT_COVERAGE &&
        yMinFit >= -PREVIEW_FIT_COVERAGE
    ) {
        panNdcY = PREVIEW_FIT_COVERAGE - yMaxFit;
    }

    return {zoom, panNdcX, panNdcY};
};

// Both helpers return the started tween so callers can supersede an in-flight
// camera move instead of racing it.
export const tweenCameraZoom = (
    camera: THREE.OrthographicCamera,
    targetZoom: number,
    duration = 500,
    onComplete?: () => void
) => {
    const tween = new TWEEN.Tween({zoom: camera.zoom}, USE_LEGACY_TWEEN_GROUP)
        .to({zoom: targetZoom}, duration)
        .onUpdate(({zoom}) => {
            camera.zoom = zoom;
            camera.updateProjectionMatrix();
        })
        .onComplete(() => onComplete?.())
        .easing(TWEEN.Easing.Cubic.Out);

    tween.start();

    return tween;
};

export const tweenCameraPosition = (
    camera: THREE.Camera,
    target: {x?: number; y?: number; z?: number},
    duration = 500,
    onComplete?: () => void
) => {
    const tween = new TWEEN.Tween(camera.position, USE_LEGACY_TWEEN_GROUP)
        .to({x: target.x, y: target.y, z: target.z}, duration)
        .onComplete(() => onComplete?.())
        .easing(TWEEN.Easing.Cubic.Out);

    tween.start();

    return tween;
};
